import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import { Box } from "@mui/system";

interface Props {
  tableHeadings: { [key: string]: string };
  rows: any[];
}

const DecreaseDailyIncomeTable = ({ tableHeadings, rows }: Props) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const headCells = Object.entries(tableHeadings);

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const STATUS_STYLES = {
    danger: {
      bg: "#FDEDED",
      color: "#D32F2F",
      border: "#D32F2F80",
    },
    warning: {
      bg: "#FFFAEB",
      color: "#DC6803",
      border: "#FFC593",
    },
    success: {
      bg: "#EDF7ED",
      color: "#2E7D32",
      border: "#2E7D3280",
    },
    default: {
      bg: "#F5F5F5",
      color: "#616161",
      border: "#E0E0E0",
    },
  } as const;

  const getStatusStyle = (percent: number) => {
    if (Number.isNaN(percent)) return STATUS_STYLES.default;
    if (percent <= 11) return STATUS_STYLES.danger;
    if (percent <= 20) return STATUS_STYLES.warning;
    return STATUS_STYLES.success;
  };

  return (
    <>
      <TableContainer className="">
        <Table
          size="small"
          stickyHeader
          sx={{ whiteSpace: "nowrap" }}
        >
          <TableHead>
            <TableRow>
              {headCells.map(([_, value], index) => (
                <TableCell key={index} align="left">
                  <TableSortLabel>
                    {value && typeof value === "string" ? value : ""}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => (
                <React.Fragment key={row.cron_id}>
                  <TableRow
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    {headCells.map(([key], cellIndex) => (
                      <TableCell key={cellIndex} align="left">
                        {key === "brand_name" ? (
                          <> {row[key] ? row[key] : "N/A"}</>
                        ) : key === "brand_id" ? (
                          <> {row[key] ? row[key] : "N/A"}</>
                        ) : key === "brand_action_date" ? (
                          <> {row[key] ? row[key] : "N/A"}</>
                        ) : key === "decreased_by" ? (
                          (() => {
                            const value = Number(row[key]);

                            if (
                              Number.isNaN(value) ||
                              row[key] === null ||
                              row[key] === undefined
                            ) {
                              return null;
                            }

                            const statusStyle = getStatusStyle(value);

                            return (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                {value > 5000 ? (
                                  <Box
                                    sx={{
                                      backgroundColor: statusStyle.bg,
                                      color: statusStyle.color,
                                      border: `1px solid ${statusStyle.border}`,
                                      borderRadius: "4px",
                                      px: "4px",
                                      py: "1px",
                                      fontSize: "14px",
                                      fontWeight: 400,
                                      display: "inline-flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    ${value.toFixed()}
                                  </Box>
                                ) : (
                                  <> ${value.toFixed()} </>
                                )}
                              </Box>
                            );
                          })()
                        ) : key === "max_reported_total_income" ? (
                          <>{row[key] ? row[key] : "N/A"} </>
                        ) : key === "current_total_income" ? (
                          <>{row[key] ? row[key] : "N/A"}</>
                        ) : row[key] ? (
                          row[key]
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </React.Fragment>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack
        spacing={1}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={rows?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          classes={{
            displayedRows: "hidden-displayed-rows", // Apply the custom class
          }}
          sx={{
            ".MuiTablePagination-actions": {
              display: "none",
            },
            ".MuiTablePagination-input": {
              marginLeft: "15px",
            },
          }}
        />
        <Pagination
          count={Math.ceil(rows?.length / rowsPerPage)}
          page={page + 1}
          showFirstButton
          showLastButton
          className="pagination-button"
          color="secondary"
          size="small"
          sx={{
            ".css-wjh20t-MuiPagination-ul": {
              justifyContent: "center",
            },
          }}
          onChange={(_, value) => handleChangePage(null, value - 1)}
        />
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={rows?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
              {
                display: "none",
              },
            ".MuiTablePagination-actions": {
              display: "none",
            },
            ".MuiTablePagination-toolbar": {
              paddingRight: "16px",
            },
          }}
        />
      </Stack>
    </>
  );
};

export default DecreaseDailyIncomeTable;
